import type { ActiveReviewerPresence } from '@shared/review-presence-api';

export const REVIEW_PRESENCE_POLL_VISIBLE_MS = 8_000;
export const REVIEW_PRESENCE_POLL_HIDDEN_MS = 60_000;

export const REVIEW_PRESENCE_DISPLAY_MAX = 4;

function compareReviewerName(a: ActiveReviewerPresence, b: ActiveReviewerPresence): number {
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    if (byName !== 0) return byName;
    return a.userId - b.userId;
}

/** Stable name order; one entry per reviewer from the API (already one row per user/cycle). */
export function sortReviewPresenceReviewers(reviewers: ActiveReviewerPresence[]): ActiveReviewerPresence[] {
    return [...reviewers].sort(compareReviewerName);
}

export function groupReviewPresenceByStore(
    reviewers: ActiveReviewerPresence[],
): Map<number, ActiveReviewerPresence[]> {
    const map = new Map<number, ActiveReviewerPresence[]>();

    for (const reviewer of reviewers) {
        const existing = map.get(reviewer.storeId);
        if (existing) {
            existing.push(reviewer);
        } else {
            map.set(reviewer.storeId, [reviewer]);
        }
    }

    for (const [storeId, list] of map) {
        map.set(storeId, sortReviewPresenceReviewers(list));
    }

    return map;
}

export function selectReviewPresenceDisplay(reviewers: ActiveReviewerPresence[]): {
    visible: ActiveReviewerPresence[];
    overflow: number;
} {
    const sorted = sortReviewPresenceReviewers(reviewers);
    if (sorted.length <= REVIEW_PRESENCE_DISPLAY_MAX) {
        return { visible: sorted, overflow: 0 };
    }
    return {
        visible: sorted.slice(0, REVIEW_PRESENCE_DISPLAY_MAX),
        overflow: sorted.length - REVIEW_PRESENCE_DISPLAY_MAX,
    };
}

export function reviewPresenceTooltipLines(reviewer: ActiveReviewerPresence): string[] {
    const lines = [reviewer.name];
    if (reviewer.campaignId != null) {
        lines.push(`Campaign ${reviewer.campaignId}`);
    } else {
        lines.push('Store review');
    }
    lines.push(reviewPresenceActivityLabel(reviewer));
    lines.push('Informational only — work is not locked.');
    return lines;
}

export function resolveReviewPresencePollInterval(isTabVisible: boolean): number {
    return isTabVisible ? REVIEW_PRESENCE_POLL_VISIBLE_MS : REVIEW_PRESENCE_POLL_HIDDEN_MS;
}

export function isReviewPresencePollEnabled(
    flagEnabled: boolean,
    cycleId: string | null,
): boolean {
    if (!flagEnabled || !cycleId || cycleId === 'all') {
        return false;
    }
    const parsed = Number(cycleId);
    return Number.isFinite(parsed) && parsed > 0;
}

export function reviewPresenceActivityLabel(reviewer: ActiveReviewerPresence): string {
    return reviewer.surface === 'evidence_drawer' ? 'Viewing evidence' : 'Reviewing';
}
